import React from "react";

/**
 * AttentionDot — a small pulsing dot with a radiating ping ring, meant to
 * be placed next to a nav link or button to draw the eye without adding
 * visual noise. Uses only Tailwind's built-in `animate-ping` + `animate-pulse`
 * so it degrades to a solid dot for users who prefer reduced motion (Tailwind
 * respects `prefers-reduced-motion` for these utilities out of the box).
 *
 * Usage:
 *   <div className="relative inline-flex items-center gap-1.5">
 *     <span>AI Cart™ Search</span>
 *     <AttentionDot />
 *   </div>
 */
interface AttentionDotProps {
  /** Tailwind color class for the dot fill, e.g. "bg-fuchsia-500". */
  color?: string;
  /** Size in Tailwind units. Default is 2 (0.5rem square). */
  size?: 1.5 | 2 | 2.5 | 3;
  /** Extra classes forwarded to the wrapper. */
  className?: string;
  /** Optional data-testid so tests can assert visibility. */
  testId?: string;
}

const SIZE_CLS: Record<number, string> = {
  1.5: "h-1.5 w-1.5",
  2: "h-2 w-2",
  2.5: "h-2.5 w-2.5",
  3: "h-3 w-3",
};

export const AttentionDot: React.FC<AttentionDotProps> = ({
  color = "bg-fuchsia-500",
  size = 2,
  className,
  testId,
}) => {
  const sz = SIZE_CLS[size] || SIZE_CLS[2];
  return (
    <span
      className={`relative inline-flex ${sz} ${className || ""}`}
      data-testid={testId}
      aria-hidden="true"
    >
      <span
        className={`animate-ping absolute inline-flex h-full w-full rounded-full ${color} opacity-70`}
      />
      <span className={`relative inline-flex rounded-full ${sz} ${color}`} />
    </span>
  );
};

export default AttentionDot;
